import {
  generateId,
  getQuantityString,
  matchMethodGroup,
  parseIngredientLine,
  ingredientsToText,
  textToIngredients,
  methodStepsToText,
  textToMethodSteps,
  type Ingredient,
  type MethodStep,
  type ParsedRecipe,
} from "@reciperoll/core";

// A dummy recipeId for text round-trips — rows don't carry a recipeId; it's
// stamped at form-submit time. Any non-empty value works for the core helpers.
const TMP = "tmp";

// --- Editor row types ------------------------------------------------------

export interface IngredientRow {
  id: string;
  isGroup: boolean;
  qty: string; // freeform, e.g. "400g", "2", "½" (ignored for group rows)
  name: string; // ingredient, or the heading text for a group row
  subQty: string; // substitute's freeform quantity; both sub fields empty = no substitute
  subName: string;
}

export interface MethodRow {
  id: string;
  isGroup: boolean;
  text: string; // step instruction, or heading text for a group row
}

export const newIngredientRow = (isGroup = false): IngredientRow => ({
  id: generateId(),
  isGroup,
  qty: "",
  name: "",
  subQty: "",
  subName: "",
});

export const newMethodRow = (isGroup = false): MethodRow => ({
  id: generateId(),
  isGroup,
  text: "",
});

// --- Rows → entities (used on save AND for bulk-edit serialization) ---------

export function ingredientRowsToEntities(
  rows: IngredientRow[],
  recipeId: string,
): Ingredient[] {
  return rows
    .filter((r) => r.name.trim())
    .map((row, index) => {
      if (row.isGroup) {
        return {
          id: row.id,
          recipeId,
          name: row.name.trim(),
          group: true,
          quantity: null,
          fraction: null,
          unitType: null,
          unit: null,
          other: null,
          substitute: null,
          sortOrder: index,
        };
      }
      // Parse the freeform qty field (e.g. "400g") into structured fields.
      const p = parseIngredientLine(row.qty.trim());
      // The substitute's quantity parses the same way; its name comes from its
      // own field (null when the name is empty — a quantity alone is noise).
      const subName = row.subName.trim();
      const pa = subName ? parseIngredientLine(row.subQty.trim()) : null;
      return {
        id: row.id,
        recipeId,
        name: row.name.trim(),
        group: false,
        quantity: p.quantity ?? null,
        fraction: p.fraction ?? null,
        unitType: p.unitType ?? null,
        unit: p.unit ?? null,
        other: p.other ?? null,
        substitute: pa
          ? {
              name: subName,
              quantity: pa.quantity ?? null,
              fraction: pa.fraction ?? null,
              unitType: pa.unitType ?? null,
              unit: pa.unit ?? null,
              other: pa.other ?? null,
            }
          : null,
        sortOrder: index,
      };
    });
}

export function methodRowsToEntities(rows: MethodRow[], recipeId: string): MethodStep[] {
  return rows
    .filter((r) => r.text.trim())
    .map((row, index) => ({
      id: row.id,
      recipeId,
      stepNumber: index + 1,
      instruction: row.text.trim(),
      group: row.isGroup,
    }));
}

// --- Entities → rows -------------------------------------------------------
// Preserve ids so an edit updates rows in place; rows dropped on save are
// tombstoned by setIngredientsForRecipe.

export function ingredientsToRows(ingredients: Ingredient[]): IngredientRow[] {
  return ingredients.map((ing) => ({
    id: ing.id,
    isGroup: !!ing.group,
    qty: ing.group ? "" : getQuantityString(ing),
    name: ing.name,
    subQty: ing.substitute ? getQuantityString(ing.substitute) : "",
    subName: ing.substitute?.name ?? "",
  }));
}

export function methodStepsToRows(steps: MethodStep[]): MethodRow[] {
  return steps.map((ms) => ({ id: ms.id, isGroup: !!ms.group, text: ms.instruction }));
}

// --- Parsed draft (import / quick paste) → rows (fresh ids) -----------------

export function parsedToIngredientRows(parsed: ParsedRecipe): IngredientRow[] {
  return parsed.ingredients.map((p) => ({
    id: generateId(),
    isGroup: !!p.group,
    qty: p.group ? "" : getQuantityString(p as unknown as Ingredient),
    name: p.name,
    subQty: p.substitute ? getQuantityString(p.substitute) : "",
    subName: p.substitute?.name ?? "",
  }));
}

export function parsedToMethodRows(parsed: ParsedRecipe): MethodRow[] {
  return parsed.methodSteps.map((line) => {
    const heading = matchMethodGroup(line);
    return { id: generateId(), isGroup: heading !== null, text: heading ?? line };
  });
}

// --- Rows ↔ bulk-edit text (reuses core's round-trip serialization) ---------

export const ingredientRowsToText = (rows: IngredientRow[]): string =>
  ingredientsToText(ingredientRowsToEntities(rows, TMP));

export const textToIngredientRows = (text: string): IngredientRow[] =>
  ingredientsToRows(textToIngredients(text, TMP));

export const methodRowsToText = (rows: MethodRow[]): string =>
  methodStepsToText(methodRowsToEntities(rows, TMP));

export const textToMethodRows = (text: string): MethodRow[] =>
  methodStepsToRows(textToMethodSteps(text, TMP));
